import {
  Attributes,
  Button,
  DataStatus,
  Dialog,
  Flag,
  Separator,
  openConfirmDialog,
  showNotification,
} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';
import {DateTimeFormat, useFormatCurrency, useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetUsersQuery} from '@dms/api/customer';
import {
  useCancelPricelistVersionMutation,
  useListPricelistVersionHistoryQuery,
  type PricelistVersionHistoryItemResponseBody,
} from '@dms/api/new-vehicle';
import {getNewVehicleEnumLabel} from '@dms/features/new-vehicle';
import {getUserName} from '@dms/shared';

import {parseDate} from 'shared/web';

import {handleCatalogueError, joinParts, t} from '../utils/catalogueUi';
import {PriceChangesTable} from './PriceChangesTable';

function useRuleText(currency: string) {
  const formatNumber = useFormatNumber();
  const formatCurrency = useFormatCurrency();
  return (item: PricelistVersionHistoryItemResponseBody) => {
    if (isNil(item.kind) || isNil(item.value)) {
      return undefined;
    }
    const amount = Number(item.value);
    const sign = amount > 0 ? '+' : '';
    const money = formatCurrency(amount, currency, Number.isInteger(amount) ? 0 : 2);
    return match(item.kind)
      .with('percentage', () => `${sign}${formatNumber(amount)} %`)
      .with('amount', () => `${sign}${money}`)
      .with('set', () =>
        t('entity.newVehicle.catalogue.history.setTo', 'Set to {amount}', {amount: money})
      )
      .exhaustive();
  };
}

function PricelistVersionEntry(props: {
  item: PricelistVersionHistoryItemResponseBody;
  changedBy: string;
  rule: string | undefined;
  currency: string;
  modelYearId: string;
  canCancel: boolean;
  onCancel: () => void;
}) {
  const [arePricesShown, setArePricesShown] = useState(false);
  const changes = props.item.changes ?? [];
  const isScheduled = props.item.activatedAt === null;
  const importerPriceList = props.item.importerPriceList;
  return (
    <VStack spacing={2} data-testid={`pricelist-version-${props.item.id}`}>
      <HStack spacing={2} align="center" justify="space-between">
        <HStack spacing={2} align="center">
          <Text alternative>{props.item.versionLabel}</Text>
          <Show when={props.item.origin === 'publish'}>
            <Flag
              label={getNewVehicleEnumLabel('modelYearStatus', 'active', t)}
              colorScheme="neutral"
              isSubtle
            />
          </Show>
          <Show when={props.item.origin === 'itemChange'}>
            <Flag
              label={getNewVehicleEnumLabel('pricelistVersionOrigin', 'itemChange', t)}
              colorScheme="neutral"
              isSubtle
            />
          </Show>
          <Show when={isScheduled}>
            <Flag
              label={t('entity.workshop.labels.scheduled', 'Scheduled')}
              colorScheme="orange"
              isSubtle
            />
          </Show>
        </HStack>
        <Show when={isScheduled && props.canCancel}>
          <Button
            variant="secondary"
            size="small"
            title={t(
              'entity.newVehicle.catalogue.history.cancelScheduled',
              'Cancel scheduled change'
            )}
            onClick={props.onCancel}
          />
        </Show>
      </HStack>
      <Attributes
        rows={[
          {
            label: t('entity.newVehicle.catalogue.adjustment.effectiveFrom', 'Effective from'),
            content: (
              <DateTimeFormat value={parseDate(props.item.effectiveFrom)} format="dateMedium" />
            ),
          },
          {
            label: t('entity.newVehicle.catalogue.changed', 'Changed'),
            content: <DateTimeFormat value={parseDate(props.item.changedAt)} format="dateMedium" />,
          },
          {label: t('entity.newVehicle.catalogue.changedBy', 'By'), value: props.changedBy},
          ...(props.rule
            ? [
                {
                  label: t('entity.newVehicle.catalogue.adjustment.change', 'Change'),
                  value: props.rule,
                },
              ]
            : []),
          {
            label: t('entity.newVehicle.catalogue.scope', 'Scope'),
            value: getNewVehicleEnumLabel('adjustmentScope', props.item.scope, t),
          },
          ...(isNotNil(importerPriceList)
            ? [
                {
                  label: t(
                    'entity.newVehicle.catalogue.importerPriceList.title',
                    'Importer price list'
                  ),
                  value:
                    joinParts([
                      importerPriceList.catalogueId,
                      importerPriceList.priceListVersion,
                    ]) || null,
                },
              ]
            : []),
          ...(changes.length > 0
            ? [
                {
                  label: t('entity.checkout.labels.items', 'Items'),
                  value: changes.length.toString(),
                },
              ]
            : []),
          {
            label: t('entity.newVehicle.catalogue.offersUnaffected', 'Offers unaffected'),
            value: props.item.configurationsUnaffected
              ? t('general.labels.yes', 'Yes')
              : t('general.labels.no', 'No'),
          },
          ...(isNotNilOrEmpty(props.item.note)
            ? [{label: t('general.labels.note', 'Note'), value: props.item.note}]
            : []),
        ]}
      />
      <Show when={changes.length > 0}>
        <HStack>
          <Button
            variant="link"
            aria-expanded={arePricesShown}
            title={
              arePricesShown
                ? t('entity.newVehicle.catalogue.history.hidePrices', 'Hide prices')
                : t('entity.newVehicle.catalogue.history.showPrices', 'Show prices ({count})', {
                    count: changes.length,
                  })
            }
            onClick={() => setArePricesShown(!arePricesShown)}
          />
        </HStack>
      </Show>
      <Show when={arePricesShown}>
        <PriceChangesTable
          changes={changes}
          currency={props.currency}
          modelYearId={props.modelYearId}
        />
      </Show>
    </VStack>
  );
}

export function PricelistVersionHistoryDialog(props: {
  isOpen: boolean;
  onClose: () => void;
  modelYearId: string;
  modelYearVersion: number;
  currency: string;
  canWrite: boolean;
}) {
  // Refetched on every open: a scheduled version may have taken effect since it was cached.
  const historyQuery = useListPricelistVersionHistoryQuery(
    {modelYearId: props.modelYearId},
    {skip: !props.isOpen, refetchOnMountOrArgChange: true}
  );
  const usersQuery = useGetUsersQuery(undefined, {skip: !props.isOpen});
  const [cancelVersion] = useCancelPricelistVersionMutation();
  const ruleText = useRuleText(props.currency);
  const userNameOf = (id: string) =>
    getUserName(usersQuery.data?.find((user) => user.id === id)) ?? id;
  const confirmCancel = (item: PricelistVersionHistoryItemResponseBody) =>
    openConfirmDialog({
      text: t(
        'entity.newVehicle.catalogue.history.cancelScheduledConfirm',
        'Cancel the scheduled pricelist {label}? The current prices stay in use.',
        {label: item.versionLabel}
      ),
      onConfirm: () =>
        void cancelVersion({
          modelYearId: props.modelYearId,
          pricelistVersionId: item.id,
          versionedWriteRequestBody: {version: props.modelYearVersion},
        })
          .unwrap()
          .then(() =>
            showNotification.success(
              t(
                'entity.newVehicle.catalogue.history.cancelled',
                'Scheduled pricelist {label} cancelled.',
                {label: item.versionLabel}
              )
            )
          )
          .catch(handleCatalogueError),
    });

  return (
    <Dialog
      isOpen={props.isOpen}
      onClose={props.onClose}
      size="large"
      title={t('entity.newVehicle.catalogue.pricelistHistory', 'Pricelist version history')}
    >
      <DataStatus
        isLoading={historyQuery.isLoading || usersQuery.isLoading}
        isError={historyQuery.isError}
        isEmpty={!historyQuery.data?.items.length}
      >
        <VStack spacing={4}>
          {historyQuery.data?.items.map((item, index) => (
            <VStack key={item.id} spacing={4}>
              <Show when={index > 0}>
                <Separator spacing={0} />
              </Show>
              <PricelistVersionEntry
                item={item}
                changedBy={userNameOf(item.changedBy)}
                rule={ruleText(item)}
                currency={props.currency}
                modelYearId={props.modelYearId}
                canCancel={props.canWrite}
                onCancel={() => confirmCancel(item)}
              />
            </VStack>
          ))}
        </VStack>
      </DataStatus>
    </Dialog>
  );
}
